/**
 * QuestionEditor — lets the teacher create, edit, and delete battle questions
 * for any standard. No code required.
 */
import { useState, useEffect } from 'react'
import { standards, questions } from '@/api/client'
import type { Standard, Question } from '@/api/client'

const QUESTION_TYPES = [
  { key: 'multiple_choice',     label: 'Multiple Choice' },
  { key: 'evidence_selection',  label: 'Evidence Selection' },
  { key: 'drag_drop',           label: 'Drag & Drop Matching' },
  { key: 'timeline',            label: 'Timeline Ordering' },
  { key: 'cause_effect',        label: 'Cause & Effect' },
  { key: 'short_response',      label: 'Short Response' },
]

export default function QuestionEditor() {
  const [allStandards, setAllStandards] = useState<Standard[]>([])
  const [selectedStd, setSelectedStd]   = useState<string>('')
  const [qList, setQList]               = useState<Question[]>([])
  const [editing, setEditing]           = useState<Question | null>(null)
  const [creating, setCreating]         = useState(false)
  const [loading, setLoading]           = useState(false)
  const [msg, setMsg]                   = useState('')

  useEffect(() => {
    standards.list().then(setAllStandards)
  }, [])

  useEffect(() => {
    if (!selectedStd) return
    setLoading(true)
    questions.manage(selectedStd).then(setQList).finally(() => setLoading(false))
  }, [selectedStd])

  async function handleSave(q: Partial<Question>) {
    setMsg('')
    try {
      if (editing) {
        await questions.update(editing.id, q)
        setMsg('✅ Question updated!')
      } else {
        await questions.create(q as any)
        setMsg('✅ Question created!')
      }
      const updated = await questions.manage(selectedStd)
      setQList(updated)
      setEditing(null)
      setCreating(false)
    } catch (err: any) {
      setMsg(`❌ ${err.message}`)
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this question?')) return
    await questions.delete(id)
    setQList(q => q.filter(x => x.id !== id))
    setMsg('🗑️ Question deleted')
  }

  const biomeColors: Record<string, string> = {
    history: '#7c3a1e', geography: '#1a4731', civics: '#1e1b4b', economics: '#451a00',
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h2 style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '12px', color: '#a78bfa', marginBottom: 20 }}>
        ✏️ Question Authoring
      </h2>

      {/* Standard selector */}
      <div className="mb-6">
        <label className="block mb-2" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>
          SELECT STANDARD
        </label>
        <select
          value={selectedStd}
          onChange={e => { setSelectedStd(e.target.value); setEditing(null); setCreating(false) }}
          className="rounded px-3 py-2 outline-none"
          style={{ background: '#1e1b4b', border: '2px solid #4338ca', color: '#e0e7ff',
                   fontFamily: 'system-ui', fontSize: '13px', minWidth: 400 }}>
          <option value="">-- Choose a standard --</option>
          {allStandards.map(s => (
            <option key={s.standard_id} value={s.standard_id}>
              {s.standard_id} · {s.boss_name} ({s.strand})
              {s.mstep_avg_pct ? ` — ${s.mstep_avg_pct}% M-STEP avg` : ''}
            </option>
          ))}
        </select>
      </div>

      {msg && (
        <div className="mb-4 px-4 py-2 rounded" style={{
          background: msg.startsWith('✅') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.startsWith('✅') ? '#34d399' : '#ef4444'}`,
          fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#e0e7ff',
        }}>
          {msg}
        </div>
      )}

      {selectedStd && !creating && !editing && (
        <div>
          {/* Standard info */}
          {(() => {
            const std = allStandards.find(s => s.standard_id === selectedStd)
            if (!std) return null
            return (
              <div className="mb-4 p-4 rounded-lg" style={{
                background: biomeColors[std.biome] || '#1e1b4b',
                border: '1px solid rgba(255,255,255,0.1)',
              }}>
                <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#fbbf24' }}>
                  {std.boss_name} · {std.strand}
                </p>
                <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#d1d5db', marginTop: 4 }}>
                  {std.full_text}
                </p>
                {std.mstep_avg_pct && (
                  <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#fbbf24', marginTop: 6 }}>
                    ⚠️ M-STEP Average: {std.mstep_avg_pct}%
                    {std.mstep_avg_pct < 40 ? ' — LOW PRIORITY' : ''}
                  </p>
                )}
              </div>
            )
          })()}

          <div className="flex justify-between items-center mb-4">
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#818cf8' }}>
              {qList.length} QUESTION{qList.length !== 1 ? 'S' : ''}
            </span>
            <button onClick={() => setCreating(true)}
              className="px-4 py-2 rounded"
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
                       background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8', cursor: 'pointer' }}>
              + ADD QUESTION
            </button>
          </div>

          {loading ? (
            <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6b7280' }}>Loading…</p>
          ) : qList.length === 0 ? (
            <div className="p-8 rounded-lg text-center" style={{ background: '#0f0f23', border: '2px dashed #1e1b4b' }}>
              <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6b7280' }}>
                No questions yet. Click "+ ADD QUESTION" to create the first one.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {qList.map(q => (
                <QuestionCard key={q.id} question={q}
                  onEdit={() => setEditing(q)}
                  onDelete={() => handleDelete(q.id)} />
              ))}
            </div>
          )}
        </div>
      )}

      {(creating || editing) && (
        <QuestionForm
          initial={editing || { standard_id: selectedStd, type: 'multiple_choice', difficulty: 2, points: 10 } as any}
          onSave={handleSave}
          onCancel={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

// ── Question Card ─────────────────────────────────────────────────────────────

function QuestionCard({ question: q, onEdit, onDelete }: {
  question: Question; onEdit: () => void; onDelete: () => void
}) {
  const typeLabel = QUESTION_TYPES.find(t => t.key === q.type)?.label || q.type
  return (
    <div className="p-4 rounded-lg" style={{
      background: '#0f0f23', border: '1px solid #1e1b4b',
    }}>
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1">
          <div className="flex gap-2 items-center mb-2">
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '6px',
                           background: '#1e1b4b', border: '1px solid #312e81',
                           padding: '2px 6px', borderRadius: 3, color: '#818cf8' }}>
              {typeLabel}
            </span>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '6px', color: '#fbbf24' }}>
              {q.points} pts
            </span>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '6px',
                           color: q.is_active ? '#34d399' : '#ef4444' }}>
              {q.is_active ? '● ACTIVE' : '○ HIDDEN'}
            </span>
            {q.school_id === null && (
              <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '5px',
                             color: '#6b7280', background: '#0a0a1a', padding: '1px 4px', borderRadius: 2 }}>
                DEFAULT
              </span>
            )}
          </div>
          <p style={{ fontFamily: 'system-ui', fontSize: '13px', color: '#d1d5db', lineHeight: 1.4 }}>
            {q.prompt}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={onEdit}
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                     background: '#1e1b4b', border: '1px solid #4338ca',
                     color: '#818cf8', padding: '4px 8px', borderRadius: 4, cursor: 'pointer' }}>
            EDIT
          </button>
          <button onClick={onDelete}
            style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                     background: '#1f0a0a', border: '1px solid #7f1d1d',
                     color: '#ef4444', padding: '4px 8px', borderRadius: 4, cursor: 'pointer' }}>
            DEL
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Question Form ─────────────────────────────────────────────────────────────

function QuestionForm({ initial, onSave, onCancel }: {
  initial: Partial<Question>
  onSave: (q: Partial<Question>) => void
  onCancel: () => void
}) {
  const [type, setType]         = useState(initial.type || 'multiple_choice')
  const [prompt, setPrompt]     = useState(initial.prompt || '')
  const [scenario, setScenario] = useState(initial.scenario || '')
  const [hint, setHint]         = useState(initial.hint || '')
  const [points, setPoints]     = useState(initial.points || 10)
  const [difficulty, setDifficulty] = useState(initial.difficulty || 2)
  const [isActive, setIsActive] = useState(initial.is_active !== false)

  // Multiple choice state
  const [mcOptions, setMcOptions] = useState<Array<{ key: string; text: string }>>(
    (initial.options_json as any) || [
      { key: 'A', text: '' }, { key: 'B', text: '' },
      { key: 'C', text: '' }, { key: 'D', text: '' },
    ]
  )
  const [mcCorrect, setMcCorrect] = useState<string>((initial.correct_json as string) || 'A')

  // Evidence selection
  const [evOptions, setEvOptions] = useState<Array<{ key: string; text: string }>>(
    type === 'evidence_selection' ? ((initial.options_json as any) || [
      { key: 'A', text: '' }, { key: 'B', text: '' },
      { key: 'C', text: '' }, { key: 'D', text: '' },
    ]) : [{ key: 'A', text: '' }, { key: 'B', text: '' }, { key: 'C', text: '' }, { key: 'D', text: '' }]
  )
  const [evCorrect, setEvCorrect] = useState<string[]>(
    type === 'evidence_selection' ? ((initial.correct_json as string[]) || []) : []
  )

  // Drag/drop + cause/effect
  const [ddSources, setDdSources] = useState<Array<{ key: string; text: string }>>(
    ((initial.options_json as any)?.sources) || [
      { key: 'S1', text: '' }, { key: 'S2', text: '' }, { key: 'S3', text: '' },
    ]
  )
  const [ddTargets, setDdTargets] = useState<Array<{ key: string; text: string }>>(
    ((initial.options_json as any)?.targets) || [
      { key: 'T1', text: '' }, { key: 'T2', text: '' }, { key: 'T3', text: '' },
    ]
  )
  const [ddCorrect, setDdCorrect] = useState<Record<string, string>>(
    (initial.correct_json as Record<string, string>) || {}
  )

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let options_json: unknown = null
    let correct_json: unknown = null

    if (type === 'multiple_choice') {
      options_json = mcOptions.filter(o => o.text.trim())
      correct_json = mcCorrect
    } else if (type === 'evidence_selection') {
      options_json = evOptions.filter(o => o.text.trim())
      correct_json = evCorrect
    } else if (type === 'drag_drop' || type === 'cause_effect') {
      options_json = {
        sources: ddSources.filter(s => s.text.trim()),
        targets: ddTargets.filter(t => t.text.trim()),
      }
      correct_json = ddCorrect
    } else if (type === 'timeline') {
      options_json = mcOptions.filter(o => o.text.trim())  // reuse MC options as items to order
      correct_json = mcOptions.filter(o => o.text.trim()).map(o => o.key)
    } else if (type === 'short_response') {
      options_json = null
      correct_json = ''
    }

    onSave({
      ...initial,
      type, prompt, scenario: scenario || null, hint: hint || null,
      points, difficulty, is_active: isActive,
      options_json, correct_json,
    })
  }

  const fieldStyle = {
    background: '#1e1b4b', border: '1px solid #4338ca', color: '#e0e7ff',
    fontFamily: 'system-ui', fontSize: '13px', borderRadius: 4, padding: '6px 10px', width: '100%',
    outline: 'none',
  }

  const labelStyle = {
    fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8',
    display: 'block', marginBottom: 4,
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
      <div className="flex justify-between items-center">
        <h3 style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '10px', color: '#fbbf24' }}>
          {initial.id ? 'EDIT QUESTION' : 'NEW QUESTION'}
        </h3>
        <button type="button" onClick={onCancel}
          style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                   background: '#1e1b4b', border: '1px solid #4338ca',
                   color: '#818cf8', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>
          ← CANCEL
        </button>
      </div>

      {/* Type */}
      <div>
        <label style={labelStyle}>QUESTION TYPE</label>
        <select value={type} onChange={e => setType(e.target.value)} style={fieldStyle}>
          {QUESTION_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>

      {/* Scenario */}
      <div>
        <label style={labelStyle}>SCENARIO / CONTEXT (optional)</label>
        <textarea value={scenario} onChange={e => setScenario(e.target.value)}
          placeholder="Provide a document excerpt, map description, or scenario for context..."
          rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
      </div>

      {/* Prompt */}
      <div>
        <label style={labelStyle}>QUESTION PROMPT *</label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
          placeholder="Write your question here..." required
          rows={2} style={{ ...fieldStyle, resize: 'vertical' }} />
      </div>

      {/* Type-specific answer options */}
      {(type === 'multiple_choice' || type === 'timeline') && (
        <div>
          <label style={labelStyle}>ANSWER OPTIONS</label>
          <div className="space-y-2">
            {mcOptions.map((opt, i) => (
              <div key={opt.key} className="flex gap-2 items-center">
                <label className="flex items-center gap-2">
                  <input type="radio" name="mc_correct" value={opt.key}
                    checked={mcCorrect === opt.key}
                    onChange={() => setMcCorrect(opt.key)}
                    style={{ accentColor: '#6366f1' }} />
                  <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#818cf8', minWidth: 20 }}>
                    {opt.key}
                  </span>
                </label>
                <input value={opt.text}
                  onChange={e => setMcOptions(opts => opts.map((o, j) => j === i ? { ...o, text: e.target.value } : o))}
                  placeholder={`Option ${opt.key}`} style={{ ...fieldStyle, flex: 1 }} />
              </div>
            ))}
          </div>
          {type === 'multiple_choice' && (
            <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '6px', color: '#6b7280', marginTop: 4 }}>
              ← Select the radio button next to the correct answer
            </p>
          )}
        </div>
      )}

      {type === 'evidence_selection' && (
        <div>
          <label style={labelStyle}>EVIDENCE CHOICES (check correct ones)</label>
          <div className="space-y-2">
            {evOptions.map((opt, i) => (
              <div key={opt.key} className="flex gap-2 items-center">
                <label className="flex items-center gap-2">
                  <input type="checkbox" value={opt.key}
                    checked={evCorrect.includes(opt.key)}
                    onChange={e => setEvCorrect(prev =>
                      e.target.checked ? [...prev, opt.key] : prev.filter(k => k !== opt.key)
                    )}
                    style={{ accentColor: '#6366f1' }} />
                  <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#818cf8', minWidth: 20 }}>
                    {opt.key}
                  </span>
                </label>
                <input value={opt.text}
                  onChange={e => setEvOptions(opts => opts.map((o, j) => j === i ? { ...o, text: e.target.value } : o))}
                  placeholder={`Evidence ${opt.key}`} style={{ ...fieldStyle, flex: 1 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {(type === 'drag_drop' || type === 'cause_effect') && (
        <div className="space-y-4">
          <div>
            <label style={labelStyle}>SOURCES (drag from)</label>
            {ddSources.map((src, i) => (
              <div key={src.key} className="flex gap-2 mb-1">
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#818cf8', paddingTop: 8, minWidth: 24 }}>
                  {src.key}
                </span>
                <input value={src.text}
                  onChange={e => setDdSources(srcs => srcs.map((s, j) => j === i ? { ...s, text: e.target.value } : s))}
                  placeholder={`Source ${src.key}`} style={fieldStyle} />
              </div>
            ))}
          </div>
          <div>
            <label style={labelStyle}>TARGETS (drop to)</label>
            {ddTargets.map((tgt, i) => (
              <div key={tgt.key} className="flex gap-2 mb-1">
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#fbbf24', paddingTop: 8, minWidth: 24 }}>
                  {tgt.key}
                </span>
                <input value={tgt.text}
                  onChange={e => setDdTargets(tgts => tgts.map((t, j) => j === i ? { ...t, text: e.target.value } : t))}
                  placeholder={`Target ${tgt.key}`} style={fieldStyle} />
              </div>
            ))}
          </div>
          <div>
            <label style={labelStyle}>CORRECT MATCHES (source → target)</label>
            <div className="space-y-1">
              {ddSources.filter(s => s.text.trim()).map(src => (
                <div key={src.key} className="flex gap-2 items-center">
                  <span style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#818cf8', minWidth: 100 }}>
                    {src.key}: {src.text.slice(0, 20)}
                  </span>
                  <span style={{ color: '#6b7280' }}>→</span>
                  <select value={ddCorrect[src.key] || ''}
                    onChange={e => setDdCorrect(prev => ({ ...prev, [src.key]: e.target.value }))}
                    style={{ ...fieldStyle, width: 'auto' }}>
                    <option value="">-- match --</option>
                    {ddTargets.filter(t => t.text.trim()).map(tgt => (
                      <option key={tgt.key} value={tgt.key}>{tgt.key}: {tgt.text.slice(0, 30)}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hint */}
      <div>
        <label style={labelStyle}>HINT (shown after wrong answer)</label>
        <input value={hint} onChange={e => setHint(e.target.value)}
          placeholder="Optional hint to guide students..."
          style={fieldStyle} />
      </div>

      {/* Metadata */}
      <div className="flex gap-6">
        <div>
          <label style={labelStyle}>POINTS</label>
          <input type="number" value={points} min={5} max={50} step={5}
            onChange={e => setPoints(Number(e.target.value))}
            style={{ ...fieldStyle, width: 80 }} />
        </div>
        <div>
          <label style={labelStyle}>DIFFICULTY</label>
          <select value={difficulty} onChange={e => setDifficulty(Number(e.target.value))}
            style={{ ...fieldStyle, width: 'auto' }}>
            <option value={1}>⭐ Easy</option>
            <option value={2}>⭐⭐ Medium</option>
            <option value={3}>⭐⭐⭐ Hard</option>
          </select>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)}
              style={{ accentColor: '#6366f1' }} />
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>
              ACTIVE
            </span>
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <button type="submit"
          className="px-6 py-2 rounded"
          style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px',
                   background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8', cursor: 'pointer' }}>
          💾 SAVE QUESTION
        </button>
        <button type="button" onClick={onCancel}
          style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px',
                   background: '#1e1b4b', border: '1px solid #4338ca',
                   color: '#818cf8', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
          CANCEL
        </button>
      </div>
    </form>
  )
}
